/**
 * 批量任务结果详情 Hook
 * 用于获取批量任务的详细信息和子任务结果
 * 支持分页和自动轮询未完成任务
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import type {
  BatchTaskInfo,
  BatchTaskGroupedResultsResponse,
  BatchTaskResultItem,
} from '@/types/task'
import { BATCH_TASK_STATUS } from '@/types/task'
import { getBatchTaskResults } from '@/services/taskService'

/**
 * 轮询配置
 */
const POLLING_CONFIG = {
  /** 轮询间隔（毫秒）- 5秒 */
  INTERVAL: 5000,
} as const

/**
 * 已完成的状态列表（不需要轮询）
 */
const FINISHED_STATUSES = [
  BATCH_TASK_STATUS.COMPLETED,
  BATCH_TASK_STATUS.PARTIAL_FAILED,
  BATCH_TASK_STATUS.FAILED,
  BATCH_TASK_STATUS.PAUSED,
  BATCH_TASK_STATUS.CANCELLED,
] as const

/**
 * Hook 返回类型
 */
export interface UseBatchTaskResultsReturn {
  /** 批次信息 */
  batchInfo: BatchTaskInfo | null
  /** 子任务结果列表 */
  results: BatchTaskResultItem[]
  /** 分组结果列表 */
  groupedResults: BatchTaskGroupedResultsResponse
  /** 是否正在加载 */
  isLoading: boolean
  /** 错误信息 */
  error: Error | null
  /** 结果总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  pageSize: number
  /** 总页数 */
  totalPages: number
  /** 切换页码 */
  setPage: (page: number) => void
  /** 刷新数据 */
  refresh: () => void
  /** 是否有更多数据 */
  hasMore: boolean
  /** 加载下一页（懒加载模式） */
  loadMore: () => void
  /** 是否正在加载更多 */
  isLoadingMore: boolean
}

/**
 * 批量任务结果详情 Hook
 * @param batchId 批量任务 ID
 * @param initialPageSize 初始每页数量（默认 100）
 * @param lazyLoad 是否启用懒加载模式（默认 true）
 * @returns 批量任务详情和子任务结果
 */
export function useBatchTaskResults(
  batchId: string | undefined,
  initialPageSize = 100,
  lazyLoad = true
): UseBatchTaskResultsReturn {
  const [batchInfo, setBatchInfo] = useState<BatchTaskInfo | null>(null)
  const [results, setResults] = useState<BatchTaskResultItem[]>([])
  const [groupedResults, setGroupedResults] = useState<BatchTaskGroupedResultsResponse>({
    items: [],
    total: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(initialPageSize)

  // 轮询定时器
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)

  /**
   * 计算总页数
   */
  const totalPages = useMemo((): number => {
    return Math.max(1, Math.ceil(total / pageSize))
  }, [total, pageSize])

  /**
   * 是否有更多数据
   */
  const hasMore = useMemo((): boolean => {
    return results.length < total
  }, [results.length, total])

  /**
   * 检查批次是否未完成
   */
  const isIncomplete = useMemo((): boolean => {
    if (batchInfo === null) return false
    return !FINISHED_STATUSES.includes(batchInfo.status as typeof FINISHED_STATUSES[number])
  }, [batchInfo])

  /**
   * 停止轮询
   */
  const stopPolling = useCallback((): void => {
    if (pollingTimerRef.current !== null) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }, [])

  /**
   * 获取批量任务详情
   * @param showLoading 是否显示加载状态
   * @param append 是否追加数据（懒加载模式）
   */
  const fetchResults = useCallback(
    async (showLoading = true, append = false): Promise<void> => {
      if (batchId === undefined) {
        return
      }

      if (showLoading) {
        if (append) {
          setIsLoadingMore(true)
        } else {
          setIsLoading(true)
        }
      }
      setError(null)

      try {
        const response = await getBatchTaskResults(batchId, page, pageSize)

        if (!isMountedRef.current) return

        setBatchInfo(response.batch_info)
        setGroupedResults(response.grouped_results)

        if (append && lazyLoad) {
          // 懒加载模式：追加数据
          setResults((prev) => [...prev, ...response.results.items])
        } else {
          // 普通分页模式：替换数据
          setResults(response.results.items)
        }

        setTotal(response.results.total)
      } catch (err) {
        if (!isMountedRef.current) return
        const errorMessage =
          err instanceof Error ? err.message : '获取任务详情失败'
        setError(new Error(errorMessage))
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    },
    [batchId, page, pageSize, lazyLoad]
  )

  /**
   * 安排下一次轮询
   */
  const schedulePolling = useCallback((): void => {
    stopPolling()

    pollingTimerRef.current = setTimeout(() => {
      void fetchResults(false)
    }, POLLING_CONFIG.INTERVAL)
  }, [fetchResults, stopPolling])

  /**
   * 刷新数据
   */
  const refresh = useCallback((): void => {
    stopPolling()
    setResults([]) // 清空现有结果
    setGroupedResults({ items: [], total: 0 })
    setPage(1)
    void fetchResults(true, false)
  }, [fetchResults, stopPolling])

  /**
   * 加载更多数据（懒加载模式）
   */
  const loadMore = useCallback((): void => {
    if (!hasMore || isLoadingMore || isLoading) {
      return
    }
    setPage((prev) => prev + 1)
  }, [hasMore, isLoadingMore, isLoading])

  // 初始加载
  useEffect(() => {
    isMountedRef.current = true
    setPage(1)
    setGroupedResults({ items: [], total: 0 })
    void fetchResults(true, false)

    return (): void => {
      isMountedRef.current = false
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId])

  // 页码变化时加载数据（懒加载时追加，普通分页时替换）
  useEffect(() => {
    if (page === 1) return // 初始加载已经处理
    void fetchResults(true, lazyLoad && page > 1)
  }, [page, fetchResults, lazyLoad])

  // 根据任务状态启动或停止轮询
  useEffect(() => {
    if (isIncomplete && isMountedRef.current) {
      schedulePolling()
    } else {
      stopPolling()
    }

    return stopPolling
  }, [isIncomplete, schedulePolling, stopPolling])

  return {
    batchInfo,
    results,
    groupedResults,
    isLoading,
    error,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
    refresh,
    hasMore,
    loadMore,
    isLoadingMore,
  }
}
